/**
 * Pure editing model for a designed sentinel plan.
 *
 * Every field the design agent chose is user-editable, so the edits live in one
 * reducer with the invariants stated once: a tag is either the target or a
 * driver but never both, per-tag maps never keep entries for tags the plan no
 * longer uses, and the method list never empties.
 */

import type {
  SentinelDesignContext,
  SentinelPlan,
} from 'features/sentinelDesign/types'
import type {
  CleaningPlan,
  ExperimentMethodKey,
  FeatureKnobs,
  SplitSpec,
} from 'features/timeSeries/experiment/types'
import { EXPERIMENT_METHOD_KEYS } from 'features/timeSeries/experiment/types'

export type PlanAction =
  /** Shallow patch for the scalar knobs (band, kNN, MLP, interval, dates…). */
  | { type: 'patch'; patch: Partial<SentinelPlan> }
  | { type: 'setTarget'; tag: string }
  | { type: 'toggleDriver'; tag: string }
  | { type: 'toggleMethod'; method: ExperimentMethodKey }
  | { type: 'setFeature'; patch: Partial<FeatureKnobs> }
  | { type: 'setCleaning'; cleaningPlan: CleaningPlan }
  | { type: 'setSplit'; split: SplitSpec }
  | { type: 'setPhaseShift'; tag: string; periods: number }
  /** Replace the whole plan (a fresh design, or "revert my edits"). */
  | { type: 'reset'; plan: SentinelPlan }
  /** Back to having no plan, e.g. after switching to another instance. */
  | { type: 'clear' }

/** Drop keys of a per-tag map that the plan no longer references. */
const pruneByTag = <TValue>(
  map: Record<string, TValue> | undefined,
  tags: readonly string[]
): Record<string, TValue> | undefined => {
  if (!map) return map
  const kept = Object.entries(map).filter(([tag]) => tags.includes(tag))
  if (kept.length === Object.keys(map).length) return map
  return Object.fromEntries(kept)
}

/** Re-establish the invariants after any change to the tag set. */
const withPrunedTagMaps = (plan: SentinelPlan): SentinelPlan => {
  const tags = [plan.target, ...plan.drivers].filter(Boolean)
  const phaseShifts = pruneByTag(plan.phase_shifts, tags) ?? {}
  const yRanges = pruneByTag(plan.y_ranges, tags)
  const cleaningTags = pruneByTag(plan.cleaning_plan?.tags, tags)
  return {
    ...plan,
    phase_shifts: phaseShifts,
    ...(plan.y_ranges === undefined ? {} : { y_ranges: yRanges }),
    cleaning_plan: {
      ...plan.cleaning_plan,
      tags: cleaningTags ?? {},
    },
  }
}

export const planReducer = (
  plan: SentinelPlan,
  action: PlanAction
): SentinelPlan => {
  switch (action.type) {
    case 'reset':
      return action.plan

    case 'clear':
      // Only meaningful on nullable state; see nullablePlanReducer.
      return plan

    case 'patch':
      return { ...plan, ...action.patch }

    case 'setFeature':
      return { ...plan, feature: { ...plan.feature, ...action.patch } }

    case 'setCleaning':
      return { ...plan, cleaning_plan: action.cleaningPlan }

    case 'setSplit':
      return { ...plan, split: action.split }

    case 'setPhaseShift': {
      const phaseShifts = { ...plan.phase_shifts }
      if (action.periods === 0) delete phaseShifts[action.tag]
      else phaseShifts[action.tag] = action.periods
      return { ...plan, phase_shifts: phaseShifts }
    }

    case 'setTarget': {
      if (!action.tag || action.tag === plan.target) return plan
      // Promoting a driver must not leave it in the driver list too.
      return withPrunedTagMaps({
        ...plan,
        target: action.tag,
        drivers: plan.drivers.filter((driver) => driver !== action.tag),
      })
    }

    case 'toggleDriver': {
      // The target can never also be a driver.
      if (!action.tag || action.tag === plan.target) return plan
      const drivers = plan.drivers.includes(action.tag)
        ? plan.drivers.filter((driver) => driver !== action.tag)
        : [...plan.drivers, action.tag]
      return withPrunedTagMaps({ ...plan, drivers })
    }

    case 'toggleMethod': {
      const on = plan.methods.includes(action.method)
      // Turning off the last method would leave nothing to fit.
      if (on && plan.methods.length === 1) return plan
      const methods = on
        ? plan.methods.filter((method) => method !== action.method)
        : EXPERIMENT_METHOD_KEYS.filter(
            (method) =>
              plan.methods.includes(method) || method === action.method
          )
      return { ...plan, methods: [...methods] }
    }
  }
}

/**
 * `planReducer` for a plan that may not exist yet: before the agent has
 * designed one there is nothing to edit, and only `reset` can create it.
 */
export const nullablePlanReducer = (
  plan: SentinelPlan | null,
  action: PlanAction
): SentinelPlan | null => {
  if (action.type === 'clear') return null
  if (plan === null) return action.type === 'reset' ? action.plan : null
  return planReducer(plan, action)
}

/** Comma/space-separated positive integers, e.g. driver lags or MLP layers. */
export const parseNumberList = (text: string): number[] =>
  text
    .split(/[,\s]+/)
    .map((part) => Number(part))
    .filter((value) => Number.isFinite(value) && value > 0)

/**
 * Fold the context bundle's ground truth onto a designed plan: the window and
 * interval are the ones the bundle was actually built over, and any tag the
 * agent named that is not in the bundle is dropped rather than sent to a run
 * that would fail on an unknown datapoint.
 */
export const reconcilePlanWithContext = (
  plan: SentinelPlan,
  context: SentinelDesignContext
): SentinelPlan => {
  const known = context.tags.map((tag) => tag.tag)
  const target = known.includes(plan.target) ? plan.target : ''
  const drivers = plan.drivers.filter(
    (driver) => known.includes(driver) && driver !== target
  )
  return withPrunedTagMaps({
    ...plan,
    target,
    drivers,
    start_date: context.window.start.slice(0, 10),
    end_date: context.window.end.slice(0, 10),
    interval_minutes: context.interval_minutes,
    interval:
      plan.interval_unit === 'minutes'
        ? context.interval_minutes
        : plan.interval,
  })
}

/** Tags the plan may reference, target-hint first so the picker reads well. */
export const planTagOptions = (
  context: SentinelDesignContext | null
): string[] => (context ? context.tags.map((tag) => tag.tag) : [])

/** Whether the plan is complete enough to run. */
export const isPlanRunnable = (plan: SentinelPlan): boolean =>
  !!plan.target && plan.drivers.length > 0 && plan.methods.length > 0

/**
 * Whether a plan read back off a file is complete enough to render.
 *
 * The declared type says these arrays exist; a plan recovered from a run that
 * died part-way, or from an older writer, is not obliged to agree. Two crashes
 * in this view came from trusting the type, so every reader of a stored plan
 * gates on this rather than on its own field.
 */
export const isRenderablePlan = (
  plan: SentinelPlan | undefined
): plan is SentinelPlan =>
  !!plan && Array.isArray(plan.drivers) && Array.isArray(plan.methods)
